import { NgClass, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Subject, Subscription } from 'rxjs';
import { ActionsOperators, AddressData } from '../../data-rules-model';
import { DataRulesService } from '../../data-rules.service';
import { ProviderFactory } from '../../lib/provider-factory';
import { IActionFieldMeta, IAddressMeta, MetaDataService } from '../../meta-data.service';
import { BaseActionComponent } from '../base-action.component';
import { AddressCountryFieldComponent } from './address-field-action/address-country-field/address-country-field.component';
import { AddressFieldActionComponent } from './address-field-action/address-field-action.component';

@Component({
  selector: 'app-action-address',
  templateUrl: './address-action.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    MatTooltip,
    AddressFieldActionComponent,
    AddressCountryFieldComponent,
    NgIf
  ],
  styleUrls: ['./address-action.component.scss']
})
export class AddressActionComponent extends BaseActionComponent implements OnInit, OnDestroy {

  addressForm: FormGroup;

  // Form Controls
  address1Control: FormControl;
  address2Control: FormControl;
  cityControl: FormControl;
  stateControl: FormControl;
  zipControl: FormControl;
  countryControl: FormControl;

  // TODO: Change to Booleans, rather than form
  // Checkbox Form Controls
  selectAllCheckbox: FormControl;
  address1ControlCheckbox: FormControl;
  address2ControlCheckbox: FormControl;
  cityControlCheckbox: FormControl;
  stateControlCheckbox: FormControl;
  zipControlCheckbox: FormControl;
  countryControlCheckbox: FormControl;

  inputCleared: Subject<void> = new Subject<void>();

  selectAllCheckboxesSubscription: Subscription;
  private address1ControlSubscription: Subscription;
  private address2ControlSubscription: Subscription;
  private cityControlSubscription: Subscription;
  private stateControlSubscription: Subscription;
  private zipControlSubscription: Subscription;

  public actionOps: typeof ActionsOperators = ActionsOperators;

  characterCountMeta: IAddressMeta[];
  address1CharOver = 0;
  address2CharOver = 0;
  cityCharOver = 0;
  stateCharOver = 0;
  zipCharOver = 0;

  countryTooltipMessage = '';
  tooltipNeeded = false;
  actionDescriptor = '';

  constructor(private formBuilder: FormBuilder, private providerFactory: ProviderFactory, protected metaDataService: MetaDataService,
              public dataRulesService: DataRulesService) {
    super(metaDataService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setUpFormControls();
    this.setUpFormCheckboxes();
    this.getCharacterCountMeta();
    this.setUpControlSubscriptions();
    this.checkTooltipAction();
    this.setUpFormValidators();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscribeFromAddressControls();
    this.removeControlsFromGroup();

    this.formGroup.controls['source'].setValidators(null); // WHY

    this.formGroup.addControl('value', new FormControl(null));
  }

  setUpFormControls(): void {
    this.removeControlsFromGroup();

    this.addressForm = this.formBuilder.group([]);
    const actionValue = this.actionData.value ? this.actionData.value : new AddressData();
    this.address1Control = new FormControl({value: actionValue.address1, disabled: !actionValue.address1});
    this.address2Control = new FormControl({value: actionValue.address2, disabled: !actionValue.address2});
    this.cityControl = new FormControl({value: actionValue.city, disabled: !actionValue.city});
    this.stateControl = new FormControl({value: actionValue.state, disabled: !actionValue.state});
    this.zipControl = new FormControl({value: actionValue.zip, disabled: !actionValue.zip});

    this.resetCountryControl(this.canEnableCountryControl(this.typeControl.value)
      ? actionValue.countryID
      : undefined
    );

    this.addressForm.addControl('address1', this.address1Control);
    this.addressForm.addControl('address2', this.address2Control);
    this.addressForm.addControl('city', this.cityControl);
    this.addressForm.addControl('state', this.stateControl);
    this.addressForm.addControl('zip', this.zipControl);
    this.addressForm.addControl('countryID', this.countryControl);

    this.formGroup.addControl('value', this.addressForm);

    this.formGroup.setValidators(this.atLeastOneFieldEnabled());
  }

  private removeControlsFromGroup() {
    this.formGroup.removeControl('address1');
    this.formGroup.removeControl('address2');
    this.formGroup.removeControl('city');
    this.formGroup.removeControl('state');
    this.formGroup.removeControl('zip');
    this.formGroup.removeControl('countryID');
    this.formGroup.removeControl('value');
  }

  private unsubscribeFromAddressControls(): void {
    this.address1ControlSubscription.unsubscribe();
    this.address2ControlSubscription.unsubscribe();
    this.cityControlSubscription.unsubscribe();
    this.stateControlSubscription.unsubscribe();
    this.zipControlSubscription.unsubscribe();
  }

  private setUpControlSubscriptions(): void {
    this.address1ControlSubscription = this.setUpControlSubscription(this.address1Control, 'address1');
    this.address2ControlSubscription = this.setUpControlSubscription(this.address2Control, 'address2');
    this.cityControlSubscription = this.setUpControlSubscription(this.cityControl, 'city');
    this.stateControlSubscription = this.setUpControlSubscription(this.stateControl, 'state');
    this.zipControlSubscription = this.setUpControlSubscription(this.zipControl, 'zip');
  }

  private setUpControlSubscription(control: FormControl, controlName: string): Subscription {
    this.checkExceededCharCount(control, controlName);
    return control.valueChanges.subscribe(value => {
      this.checkExceededCharCount(control, controlName);
      if (value) {
        this.addressForm.markAsDirty();
      }
    });
  }

  atLeastOneFieldEnabled(): ValidatorFn {
    return (): ValidationErrors | null => {
      return this.checkboxesChecked(true).length === 0 ? {incorrect: true} : null;
    };
  }

  checkboxesChecked(enabled: boolean): any[] {
    return Object.keys(this.addressForm.controls).filter(checkbox => this.addressForm.get(checkbox).enabled === enabled);
  }

  setUpFormCheckboxes(): void {
    if (this.selectAllCheckboxesSubscription) {
      this.selectAllCheckboxesSubscription.unsubscribe();
    }
    this.selectAllCheckbox = new FormControl();

    this.selectAllCheckboxesSubscription = this.selectAllCheckbox.valueChanges.subscribe(selectValue => {
      this.toggleAllCheckboxes(selectValue);
    });

    const actionValue = this.actionData.value || new AddressData();
    this.address1ControlCheckbox = new FormControl(!!actionValue.address1);
    this.address2ControlCheckbox = new FormControl(!!actionValue.address2);
    this.cityControlCheckbox = new FormControl(!!actionValue.city);
    this.stateControlCheckbox = new FormControl(!!actionValue.state);
    this.zipControlCheckbox = new FormControl(!!actionValue.zip);
    this.countryControlCheckbox = new FormControl(
      !!actionValue.countryID && this.canEnableCountryControl(this.typeControl.value)
    );

    if (this.canEnableCountryControl(this.typeControl.value)) {
      this.countryControlCheckbox.enable();
      this.countryTooltipMessage = 'Field currently disabled. Select to enable field to modify';
    } else {
      this.countryControlCheckbox.disable();
      this.countryTooltipMessage = 'Field currently disabled. Can be used only for REPLACE action type';
    }

    this.typeControl.valueChanges.subscribe(value => {
      if (this.canEnableCountryControl(value)) {
        this.countryControlCheckbox.enable();
        this.countryTooltipMessage = 'Field currently disabled. Select to enable field to modify';
      } else {
        if (this.countryControl.value) {
          this.resetCountryControl();
        }
        this.countryControlCheckbox.disable();
        this.countryTooltipMessage = 'Field currently disabled. Can be used only for REPLACE action type';
      }
    });
  }

  resetCountryControl(countryID?: string) {
    this.countryControl = new FormControl({
      value: countryID,
      disabled: countryID === undefined
    }, Validators.required);

  }

  canEnableCountryControl(actionType: string): boolean {
    return actionType === this.dataRulesService.actionOpName(this.actionOps.REPLACE);
  }

  toggleAllCheckboxes(selectAll: boolean) {
    this.addressForm.markAsDirty();

    const pairs: { checkbox: FormControl, control: FormControl }[] = [
      { checkbox: this.address1ControlCheckbox, control: this.address1Control },
      { checkbox: this.address2ControlCheckbox, control: this.address2Control },
      { checkbox: this.cityControlCheckbox, control: this.cityControl },
      { checkbox: this.stateControlCheckbox, control: this.stateControl },
      { checkbox: this.zipControlCheckbox, control: this.zipControl }
    ];

    if (this.canEnableCountryControl(this.typeControl.value)) {
      pairs.push({ checkbox: this.countryControlCheckbox, control: this.countryControl });
    }

    pairs.forEach(({ checkbox, control }) => {
      checkbox.setValue(selectAll);
      if (selectAll) {
        control.enable();
        if (!control.value) {
          control.setValue('');
        }
      } else {
        control.setValue('');
        control.disable();
      }
    });
  }

  toggleDisabledInputs(control: FormControl, controlName?: string): void {
    this.addressForm.markAsDirty();
    if (!control.enabled) {
      control.enable();
      this.addressForm.addControl(controlName, control);
      // Set field to an empty string
      if (!control.value) {
        control.setValue('');
      }
    } else {
      if (controlName === 'countryID') {
        this.inputCleared.next();
      }
      this.addressForm.removeControl(controlName);
      control.setValue('');
      control.disable();
    }
  }

  private getCharacterCountMeta(): void {
    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        this.characterCountMeta = filteredMeta.filter(fm => fm.fieldName === 'address')[0].addressFields;
      });
  }

  private checkExceededCharCount(formControl: FormControl, controlName: string): void {
    const charCount = this.characterCountMeta.find((field) => field.fieldName === controlName).maxLength;
    let numberOver = 0;
    if (formControl.value && formControl.value.length > charCount)
      numberOver = formControl.value.length - charCount;

    if (controlName === 'address1')
      this.address1CharOver = numberOver;
    if (controlName === 'address2')
      this.address2CharOver = numberOver;
    if (controlName === 'city')
      this.cityCharOver = numberOver;
    if (controlName === 'state')
      this.stateCharOver = numberOver;
    if (controlName === 'zip')
      this.zipCharOver = numberOver;
  }

  checkTooltipAction() {
    const operator: ActionsOperators = ActionsOperators[this.typeControl.value];
    switch (operator) {
      case ActionsOperators.APPEND:
        this.tooltipNeeded = true;
        this.actionDescriptor = 'appended';
        break;
      case ActionsOperators.PREPEND:
        this.tooltipNeeded = true;
        this.actionDescriptor = 'prepended';
        break;
      case ActionsOperators.SUBSTITUTE:
        this.tooltipNeeded = true;
        this.actionDescriptor = 'substituted';
        break;
      case ActionsOperators.REPLACE:
        this.tooltipNeeded = true;
        this.actionDescriptor = 'replaced';
        break;
    }
  }

  private setUpFormValidators() {
    this.address1Control.setValidators(Validators.required);
    this.address2Control.setValidators(Validators.required);
    this.cityControl.setValidators(Validators.required);
    this.stateControl.setValidators(Validators.required);
    this.zipControl.setValidators(Validators.required);
  }
}
